import { SideNavigationProps } from '@cloudscape-design/components';
import {
    NAV_GRAPH_ROOT_NODE_ID,
    NAV_GRAPH_ROOT_NODE_ID_V2,
    USE_NAV_GRAPH_V2,
} from 'src/components/providers/NavGraphProvider';
import { ENGLISH_LANGUAGE_NAME } from 'src/constants';
import { ContentObjects } from 'src/types/contentObject';
import { ArticleDetails } from 'src/types/learningDetails';
import {
    FilterItem,
    FilterParams,
    NavGraph,
    NavGraphItem,
    PathSegment,
} from 'src/types/navGraph';
import { PreferenceLocation, PreferenceSelection } from 'src/types/preferences';
import { FilterItem as HiearchyFilterItem } from 'src/components/HierarchyFilters';
import { SearchResult } from 'src/types/searchResult';
import { get } from 'lodash';

/* steps
- Get full navgraph
- Map through node children recursively
    - If current object has children, also map through those children

   filterChildrenByClassificationPath 
*/

const LANGUAGE_NAME_FILTER_VALUE_MAP: Record<
    PreferenceLocation['name'],
    string
> = {
    Arabic: 'Arabic',
    Czech: 'Czech',
    'English (US)': 'English',
    'French (Canada)': 'Frenchcanadian',
    'French (France)': 'French',
    German: 'German',
    Italian: 'Italian',
    Japanese: 'Japanese',
    Polish: 'Polish',
    Spanish: 'Spanish',
    Portuguese: 'Portuguese',
};

const checkLocationFilter = (
    filterParam: string,
    selectedUserPreferences: PreferenceSelection,
    shouldFilterDocuments: boolean = false
) => {
    const isMatchingLanguage =
        LANGUAGE_NAME_FILTER_VALUE_MAP[
            selectedUserPreferences.language.name
        ] === filterParam;

    if (shouldFilterDocuments) {
        return (
            isMatchingLanguage || ENGLISH_LANGUAGE_NAME.includes(filterParam)
        );
    }
    return isMatchingLanguage;
};

export const doesPreferenceMatchFilter = (
    filterParams: FilterParams,
    selectedUserPreferences: PreferenceSelection,
    filterParamName: 'languages' | 'regions' | 'countries' | 'sites' | 'roles',
    userPreferenceName: 'language' | 'region' | 'country' | 'site' | 'role',
    shouldFilterDocuments: boolean
): boolean => {
    // if user selects all roles, and no roles in document = DO NOT SHOW
    if (
        filterParamName === 'roles' &&
        selectedUserPreferences.role.name === 'All Roles'
    ) {
        return true;
    }

    // if user has selected "All sites" = SHOW / do not filter by site at all
    if (filterParamName === 'sites') {
        if (
            selectedUserPreferences.site?.name === 'All sites' ||
            !filterParams['sites'].length
        ) {
            return true;
        }
    }

    // no site* selected in preferences, no site in document filters = SHOW
    if (
        !selectedUserPreferences[userPreferenceName] &&
        !filterParams[filterParamName].length
    ) {
        return true;
        // no site selected in preferences, site in document filters = SHOW (not true for role)
    } else if (
        !selectedUserPreferences[userPreferenceName] &&
        filterParams[filterParamName].length
    ) {
        return true;
        // site selected in preferences, no site in document filters = DO NOT SHOW
    } else if (
        selectedUserPreferences[userPreferenceName] &&
        !filterParams[filterParamName].length
    ) {
        return false;
    }

    // site selected in preferences,  same site in document filters = SHOW
    // site selected in preferences,  different site in document filters = DO NOT SHOW
    if (filterParamName === 'languages') {
        return !!(filterParams[filterParamName] as string[]).filter(
            (f: string) =>
                checkLocationFilter(
                    f,
                    selectedUserPreferences,
                    shouldFilterDocuments
                )
        ).length;
    } else {
        return !!(filterParams[filterParamName] as FilterItem[]).filter(
            (f: PreferenceLocation) =>
                f.path === selectedUserPreferences[userPreferenceName]?.path
        ).length;
    }

    //* site used as an example, but this is true for all filters
};

const getFilteredDocuments = (
    childDocuments: any,
    selectedUserPreferences: PreferenceSelection,
    shouldFilterDocuments: boolean
) => {
    const documentNames = Object.keys(childDocuments);
    let filteredDocuments: any = {};

    documentNames.forEach((name: string) => {
        const filterParams = childDocuments[`${name}`].filterParams;

        const documentHasSelectedRegion = doesPreferenceMatchFilter(
            filterParams,
            selectedUserPreferences,
            'regions',
            'region',
            shouldFilterDocuments
        );
        const documentHasSelectedCountry = doesPreferenceMatchFilter(
            filterParams,
            selectedUserPreferences,
            'countries',
            'country',
            shouldFilterDocuments
        );
        const documentHasSelectedSite = doesPreferenceMatchFilter(
            filterParams,
            selectedUserPreferences,
            'sites',
            'site',
            shouldFilterDocuments
        );
        const documentHasSelectedRole = doesPreferenceMatchFilter(
            filterParams,
            selectedUserPreferences,
            'roles',
            'role',
            shouldFilterDocuments
        );
        const documentHasSelectedLanguage = doesPreferenceMatchFilter(
            filterParams,
            selectedUserPreferences,
            'languages',
            'language',
            shouldFilterDocuments
        );

        if (
            documentHasSelectedRegion &&
            documentHasSelectedCountry &&
            documentHasSelectedSite &&
            documentHasSelectedLanguage &&
            documentHasSelectedRole
        ) {
            filteredDocuments[name] = childDocuments[`${name}`];
        }
    });

    return filteredDocuments;
};

const filterChildrenByClassificationPath = (
    navGraphBlock: any,
    selectedUserPreferences: PreferenceSelection,
    shouldFilterDocuments: boolean
) => {
    if (navGraphBlock && navGraphBlock.children) {
        const navGraphBlockKeys = Object.keys(navGraphBlock?.children);

        navGraphBlockKeys.forEach((block) => {
            if (navGraphBlock.children[block]?.documents) {
                if (navGraphBlock.documents) {
                    const filteredParentDocuments = getFilteredDocuments(
                        navGraphBlock.documents,
                        selectedUserPreferences,
                        shouldFilterDocuments
                    );
                    navGraphBlock.documents = filteredParentDocuments;
                }

                const filteredBlockDocuments = getFilteredDocuments(
                    navGraphBlock.children[block].documents,
                    selectedUserPreferences,
                    shouldFilterDocuments
                );
                navGraphBlock.children[block].documents =
                    filteredBlockDocuments;
            }

            if (navGraphBlock.children[block]?.children) {
                filterChildrenByClassificationPath(
                    navGraphBlock.children[block],
                    selectedUserPreferences,
                    shouldFilterDocuments
                );
            }
        });
    }
};

export const removeEmptyGraphBlocks = (graph: NavGraph) => {
    let keepRunning = false;

    Object.keys(graph).forEach((key) => {
        if (
            !Object.keys(graph[key]?.documents || {}).length &&
            !Object.keys(graph[key]?.children || {}).length
        ) {
            delete graph[key];
            keepRunning = true;
        } else if (Object.keys(graph[key]?.children || {}).length) {
            removeEmptyGraphBlocks(graph[key].children!);
        }
    });

    if (keepRunning) {
        removeEmptyGraphBlocks(graph);
    }
};

export const cleanNavGraph = (navGraph: NavGraph) => {
    // Initial traversal of the navGraph to remove root nodes (no children or documents)
    removeEmptyGraphBlocks(navGraph);
    // Second traversal to remove any parents that were made empty by the first traversal
    removeEmptyGraphBlocks(navGraph);
};

export const getFilteredNavGraph = (
    fullNavGraph: NavGraph,
    selectedUserPreferences: PreferenceSelection,
    shouldFilterDocuments: boolean
) => {
    if (USE_NAV_GRAPH_V2) {
        // v2: Filter documents by language
        if (fullNavGraph) {
            const filterDocumentsByLanguage = (node: NavGraphItem) => {
                if (node.documents) {
                    const filteredDocs: { [key: string]: any } = {};
                    Object.entries(node.documents).forEach(([key, doc]) => {
                        const docLanguage = (doc as any).languages;
                        const userLanguage =
                            selectedUserPreferences.language.name;

                        const isMatch =
                            userLanguage === 'English (US)'
                                ? [
                                      'English',
                                      'English (United States)',
                                  ].includes(docLanguage)
                                : LANGUAGE_NAME_FILTER_VALUE_MAP[
                                      userLanguage
                                  ] === docLanguage;

                        const includeEnglish =
                            shouldFilterDocuments &&
                            ENGLISH_LANGUAGE_NAME.includes(docLanguage);

                        if (isMatch || includeEnglish) {
                            filteredDocs[key] = doc;
                        }
                    });
                    node.documents = filteredDocs;
                }

                if (node.children) {
                    Object.values(node.children).forEach(
                        filterDocumentsByLanguage
                    );
                }
            };

            Object.values(fullNavGraph).forEach(filterDocumentsByLanguage);
        }
        return;
    }

    if (fullNavGraph) {
        const navGraphKeys = Object.keys(fullNavGraph);

        navGraphKeys.forEach((key) => {
            return filterChildrenByClassificationPath(
                fullNavGraph[key],
                selectedUserPreferences,
                shouldFilterDocuments
            );
        });

        cleanNavGraph(fullNavGraph);
    }
};

export const buildBreadcrumbs = (
    articleData: ArticleDetails | SearchResult,
    navGraph?: NavGraph
) => {
    // TODO: at this time not sure how to enable breadcrumbs in V2 as previous solutions relied directly on
    // path attributes from xyleme api for classification ids. further investigation is needed
    // into the xyleme API to enable equivalent paths for the folder ids as opposed to classification ids
    if (USE_NAV_GRAPH_V2) {
        return [];
    }

    // v1: Parse classification path
    const correctClassification = articleData?.classifications.find((cl) =>
        cl.path.startsWith(`${NAV_GRAPH_ROOT_NODE_ID}/`)
    );

    // classification path split by / ex: 357/358/359/361/ => ['357', '358', '359', '361', '']
    const splitClassifications = correctClassification?.path.split('/');

    // remove 357 from array since it's the entire navgraph and remove empty string from end of array
    splitClassifications?.pop();
    splitClassifications?.splice(0, 1);

    const breadcrumbNames = [] as string[];

    /*
        splitClassificationIndex: index of child ID in splitClassifications array
        childrenCollection: collection of navGraph nodes to look through for path piece at splitClassificationIndex
    */
    const addBreadcrumbFromPath = (
        splitClassificationIndex: number,
        childrenCollection: any
    ) => {
        if (navGraph && splitClassifications?.length) {
            const child =
                childrenCollection[
                    splitClassifications[splitClassificationIndex]
                ];

            breadcrumbNames.push(child?.name);

            if (
                child?.children &&
                splitClassificationIndex < splitClassifications.length + 1
            ) {
                const childrenOfChild = child?.children;
                addBreadcrumbFromPath(
                    splitClassificationIndex + 1,
                    childrenOfChild
                );
            }
        }
    };

    addBreadcrumbFromPath(0, navGraph);
    return breadcrumbNames;
};

export const findDuplicates = (
    contentObjects: ContentObjects[],
    seen = new Set()
): string[] => {
    const duplicates: string[] = [];

    contentObjects.forEach((contentObject) => {
        const key = contentObject.lcmsXmlGUID;
        // Only get duplicates for base
        if (seen.has(key)) {
            duplicates.push(contentObject.lcmsXmlGUID);
        } else if (!contentObject.topics?.length) {
            seen.add(key);
        }

        if (contentObject.topics?.length) {
            duplicates.push(...findDuplicates(contentObject.topics, seen));
        }
    });

    return duplicates;
};
export const buildDetailsSideNav = (
    contentObjects: ContentObjects[],
    duplicates: Set<string>,
    isChild = false
): SideNavigationProps.Item[] => {
    return contentObjects.map((co) => {
        const sideNavSection =
            isChild && !co.topics?.length
                ? {
                      text: co.name,
                      href: `#/${co.lcmsXmlGUID}/${encodeURIComponent(
                          `${
                              duplicates.has(co.lcmsXmlGUID)
                                  ? `${co.id}-${co.name}`
                                  : co.name
                          }`
                      )}`,
                      type: co.topics?.length
                          ? 'expandable-link-group'
                          : 'link',
                      items: co.topics?.length
                          ? buildDetailsSideNav(co.topics, duplicates, true)
                          : undefined,
                  }
                : {
                      text: co.name,
                      type: co.topics?.length
                          ? 'expandable-link-group'
                          : 'link',
                      items: co.topics?.length
                          ? buildDetailsSideNav(co.topics, duplicates, true)
                          : undefined,
                  };
        if (!sideNavSection.items || !sideNavSection.items.length) {
            delete sideNavSection.items;
        }

        return sideNavSection;
    }) as SideNavigationProps.Item[];
};

export const buildHrefMap = (
    contentObjects: ContentObjects[],
    duplicates: Set<string>
): Map<string, string> => {
    const hrefArray: string[] = [];
    const hrefs = contentObjects.map((co) => {
        if (co.topics?.length) {
            return buildHrefMap(co.topics, duplicates);
        } else {
            const isDuplicate = duplicates.has(co.lcmsXmlGUID);
            const key = isDuplicate
                ? `${co.id}-${co.lcmsXmlGUID}`
                : co.lcmsXmlGUID;
            const value = isDuplicate ? `${co.id}-${co.name}` : co.name;
            hrefArray.push(value);
            return new Map([
                [key, `#/${co.lcmsXmlGUID}/${encodeURIComponent(value)}`],
            ]);
        }
    });
    return hrefs.reduce(
        (map1, map2) =>
            new Map([
                ...Array.from(map1.entries()),
                ...Array.from(map2.entries()),
            ]),
        new Map([])
    );
};

export const buildOrderedSubtopicList = (
    articleData: ArticleDetails
): { subtopicIds: string[]; duplicateSubtopics: string[] } => {
    const subtopicIds: string[] = [];
    const duplicateSubtopics: string[] = [];
    if (articleData.contentObjects.lessons?.length) {
        const dataContentObjects = articleData.contentObjects.lessons;

        const addTopicIdsToListInOrder = (cos: ContentObjects[]) => {
            cos.forEach((co) => {
                if (co.topics?.length) {
                    addTopicIdsToListInOrder(co.topics);
                } else {
                    duplicateSubtopics.push(`${co.id}-${co.lcmsXmlGUID}`);
                    subtopicIds.push(co.lcmsXmlGUID);
                }
            });
        };

        addTopicIdsToListInOrder(dataContentObjects);
    }

    return { subtopicIds, duplicateSubtopics };
};

export const generateFilter = (navItem: NavGraphItem): HiearchyFilterItem => {
    const childrenItems = Object.values(navItem.children || {})
        .sort((child1, child2) => {
            const child1NavOrder =
                child1.customAttributes
                    ?.find((attr) => attr.id === 19)
                    ?.value.replace(/[^0-9.]/g, '') || undefined;
            const child2NavOrder =
                child2.customAttributes
                    ?.find((attr) => attr.id === 19)
                    ?.value.replace(/[^0-9.]/g, '') || undefined;

            if (child1NavOrder && child2NavOrder) {
                if (child1NavOrder === child2NavOrder)
                    return child1.name.localeCompare(child2.name);
                return parseInt(child1NavOrder) < parseInt(child2NavOrder)
                    ? -1
                    : 1;
            } else if (child1NavOrder || child2NavOrder) {
                return child1NavOrder ? -1 : 1;
            }
            return child1.name.localeCompare(child2.name);
        })
        .map((item) => {
            // v2: build path from pathSegments IDs
            const path =
                USE_NAV_GRAPH_V2 && item.pathSegments
                    ? item.pathSegments.map((seg) => seg.id).join('/')
                    : item.path;

            return {
                title: item.name || '',
                path,
                dateUpdated: item.dateUpdated || '',
                description: item.description || '',
                image: item.pictureUrl || '',
                isDynamic: item.isDynamic,
            };
        });

    const documentItems = Object.values(navItem.documents || {})
        .sort((doc1, doc2) => {
            // find "Nav-Order-In-Card-Or-List" custom attribute
            const doc1NavOrder =
                doc1.customAttributes
                    ?.find((attr) => attr.id === 19)
                    ?.value.replace(/[^0-9.]/g, '') || undefined;
            const doc2NavOrder =
                doc2.customAttributes
                    ?.find((attr) => attr.id === 19)
                    ?.value.replace(/[^0-9.]/g, '') || undefined;

            if (doc1NavOrder && doc2NavOrder) {
                if (doc1NavOrder === doc2NavOrder)
                    return doc1.name.localeCompare(doc2.name);
                return parseInt(doc1NavOrder) < parseInt(doc2NavOrder) ? -1 : 1;
            } else if (doc1NavOrder || doc2NavOrder) {
                return doc1NavOrder ? -1 : 1;
            }
            return doc1.name.localeCompare(doc2.name);
        })
        .map((document) => {
            return {
                title: document.latestVersion?.fileName || document.name || '',
                documentId: document.id,
                versionId: document.latestVersion.id,
                dateUpdated: document.dateUpdated || '',
                externalLinkUrl: document.externalLinkUrl,
                description: document.description || '',
                image: document.pictureUrl || '',
                isDynamic: document.isDynamic,
            };
        });

    return {
        title: navItem.name,
        itemSelected: null,
        items: [...childrenItems, ...documentItems],
    };
};

export const getItemByPath = (path: string, filteredGraph: NavGraph) => {
    if (USE_NAV_GRAPH_V2) {
        // v2: path is slash-separated IDs
        const pathIds = path.split('/').filter(Boolean);
        // remove root if present
        if (pathIds[0] === String(NAV_GRAPH_ROOT_NODE_ID_V2)) {
            pathIds.shift();
        }

        let current: NavGraphItem | undefined;
        pathIds.forEach((id, index) => {
            current = index === 0 ? filteredGraph[id] : current?.children?.[id];
        });
        return current as NavGraphItem;
    }

    // v1: legacy lodash path
    const pathArr = path
        .split('/')
        .slice(1)
        .filter(Boolean)
        .join('-children-')
        .split('-');
    return get(filteredGraph, pathArr) as NavGraphItem;
};

export const makeInitialFilter = (
    filteredGraph: NavGraph
): HiearchyFilterItem[] => {
    const children = Object.values(filteredGraph);
    children.sort((child1, child2) => {
        // find "Nav-Order-In-Card-Or-List" custom attribute
        const child1NavOrder =
            child1.customAttributes
                ?.find((attr) => attr.id === 19)
                ?.value.replace(/[^0-9.]/g, '') || undefined;
        const child2NavOrder =
            child2.customAttributes
                ?.find((attr) => attr.id === 19)
                ?.value.replace(/[^0-9.]/g, '') || undefined;

        if (child1NavOrder && child2NavOrder) {
            if (child1NavOrder === child2NavOrder)
                return child1.name.localeCompare(child2.name);
            return parseInt(child1NavOrder) < parseInt(child2NavOrder) ? -1 : 1;
        } else if (child1NavOrder || child2NavOrder) {
            return child1NavOrder ? -1 : 1;
        }
        return child1.name.localeCompare(child2.name);
    });

    return [
        {
            title: children.map((child) => child.name).join(' / '),
            itemSelected: null,
            items: children.map((child) => {
                // v2: build path from pathSegments IDs
                const path =
                    USE_NAV_GRAPH_V2 && child.pathSegments
                        ? child.pathSegments.map((seg) => seg.id).join('/')
                        : child.path;

                return {
                    title: child?.name || '',
                    path,
                    description: child?.description || '',
                    image: child?.pictureUrl || '',
                };
            }),
        },
    ];
};
